export type MigrationStepStatus = 'completed' | 'current' | 'failed' | 'approval' | 'pending';

export interface MigrationProgressStep {
    label: string;
    subLabel?: string | undefined;
    description: string;
    status: MigrationStepStatus;
}

export interface MigrationStepInput {
    /** The migration's overall status, stringified (e.g. 'ExportInProgress', 'completed', 'PartiallyCompleted'). */
    status: string;
    /** True when the transform stage was skipped (Job2Transform.status === 'Skipped'), which omits the Transform step. */
    transformationsSkipped: boolean;
    /**
     * True when this is a dry-run PREVIEW (not a snapshot, Job3Import.DryRun === true). A dry run records the
     * migration as Complete so a preview isn't mislabeled "Failed", but it applies nothing — so the Import and
     * Complete steps must NOT render as a successful real import.
     */
    isDryRunPreview: boolean;
}

/**
 * Builds the Export → Transform → Import → Complete progress steps for the migration (non-snapshot) view.
 * Extracted verbatim from the modal step-builder so the dry-run behaviour is unit-testable without rendering
 * the full modal. Pure and side-effect free.
 */
export function buildMigrationProgressSteps(input: MigrationStepInput): MigrationProgressStep[] {
    const { status: incomingStatus, transformationsSkipped, isDryRunPreview } = input;

    const exportSubLabel =
        incomingStatus === 'ExportQueued'
            ? 'Queued'
            : incomingStatus === 'ExportInProgress'
                ? 'In Progress'
                : undefined;

    const exportStatus: MigrationStepStatus =
        incomingStatus === 'ExportFailed'
            ? 'failed'
            : ['ExportQueued', 'ExportInProgress'].includes(incomingStatus)
                ? 'current'
                : incomingStatus === 'ExportComplete'
                    ? 'approval'
                    : [
                        'TransformationQueued',
                        'TransformationInProgress',
                        'TransformationComplete',
                        'TransformationFailed',
                        'ImportQueued',
                        'ImportInProgress',
                        'ImportFailed',
                        'completed',
                        'PartiallyCompleted',
                        'failed'
                    ].includes(incomingStatus)
                        ? 'completed'
                        : 'pending';

    const transformSubLabel =
        incomingStatus === 'TransformationQueued'
            ? 'Queued'
            : incomingStatus === 'TransformationInProgress'
                ? 'In Progress'
                : undefined;

    const transformStatus: MigrationStepStatus =
        incomingStatus === 'TransformationFailed'
            ? 'failed'
            : ['TransformationQueued', 'TransformationInProgress'].includes(incomingStatus)
                ? 'current'
                : incomingStatus === 'TransformationComplete'
                    ? 'approval'
                    : [
                        'ImportQueued',
                        'ImportInProgress',
                        'ImportFailed',
                        'completed',
                        'PartiallyCompleted'
                    ].includes(incomingStatus)
                        ? 'completed'
                        : 'pending';

    const importSubLabel =
        incomingStatus === 'ImportQueued'
            ? 'Queued'
            : incomingStatus === 'ImportInProgress'
                ? 'In Progress'
                : undefined;

    // A dry run applies nothing, so Import/Complete are never "done" — the real import is still pending.
    const importStatus: MigrationStepStatus =
        isDryRunPreview
            ? 'pending'
            : incomingStatus === 'ImportFailed'
                ? 'failed'
                : ['ImportQueued', 'ImportInProgress'].includes(incomingStatus)
                    ? 'current'
                    : ['completed', 'PartiallyCompleted'].includes(incomingStatus)
                        ? 'completed'
                        : 'pending';

    const completeStatus: MigrationStepStatus =
        isDryRunPreview
            ? 'pending'
            : incomingStatus === 'PartiallyCompleted'
                ? 'current'
                : incomingStatus === 'completed'
                    ? 'completed'
                    : 'pending';

    return [
        {
            label: 'Export',
            subLabel: exportSubLabel,
            description:
                exportStatus === 'current'
                    ? 'Exporting resources from the source organization'
                    : exportStatus === 'approval'
                        ? 'Export complete — awaiting your approval to proceed'
                        : exportStatus === 'completed'
                            ? 'Resources exported successfully'
                            : exportStatus === 'failed'
                                ? 'Export encountered errors'
                                : 'Resources will be exported from the source organization',
            status: exportStatus
        },
        ...(!transformationsSkipped ? [{
            label: 'Transform',
            subLabel: transformSubLabel,
            description:
                transformStatus === 'current'
                    ? 'Applying transformations to exported resources'
                    : transformStatus === 'approval'
                        ? 'Transformation complete — awaiting your approval to proceed'
                        : transformStatus === 'completed'
                            ? 'Transformations applied successfully'
                            : transformStatus === 'failed'
                                ? 'Transformation encountered errors'
                                : 'Transformations will be applied to exported resources',
            status: transformStatus
        }] : []),
        {
            label: 'Import',
            subLabel: isDryRunPreview ? 'Preview' : importSubLabel,
            description:
                isDryRunPreview
                    ? 'Dry-run preview only — no resources were imported'
                    : importStatus === 'current'
                        ? 'Importing resources into the target organization'
                        : importStatus === 'completed'
                            ? 'Resources imported successfully'
                            : importStatus === 'failed'
                                ? 'Import encountered errors'
                                : 'Resources will be imported into the target organization',
            status: importStatus
        },
        {
            label: 'Complete',
            subLabel: completeStatus === 'current' ? 'Partial' : undefined,
            description:
                isDryRunPreview
                    ? 'Run the real import to complete the migration'
                    : completeStatus === 'completed'
                        ? 'Migration completed successfully'
                        : completeStatus === 'current'
                            ? 'Migration partially completed'
                            : 'Migration will be marked complete',
            status: completeStatus
        }
    ];
}
